/**
 * Backend abstraction — routes vault operations through either:
 *   - Web mode: WASM crypto + HTTP API (existing flow)
 *   - Tauri mode: native Rust backend via invoke()
 *
 * Pages import from this module instead of directly calling crypto.js + api.js.
 * Complex flows (grants, drops, wills) remain web-only for now.
 */

export const isTauri = !!window.__TAURI_INTERNALS__;

function invoke(cmd, args) {
  return window.__TAURI_INTERNALS__.invoke(cmd, args);
}

// ─── Web backend (existing flow) ─────────────────────────────────────────────

import { get, post, del, getRaw } from './api.js';
import {
  initWasm, hasKeys, deriveAndStoreKeys, clearKeys,
  encryptItem, decryptItem, decryptItemRaw, encryptFileBytes,
  decryptFileBytes, getUserId, setUserId,
} from './crypto.js';

const webBackend = {
  mode: 'web',

  async init() {
    await initWasm();
  },

  hasKeys() {
    return hasKeys();
  },

  async unlock(password, salt, encryptedPrivateKey, pubKey, encryptedMasterKey) {
    deriveAndStoreKeys(password, salt, encryptedPrivateKey, pubKey, encryptedMasterKey);
  },

  lock() {
    clearKeys();
  },

  async listItems() {
    return get('/items');
  },

  async createItem(envelope) {
    const enc = encryptItem(JSON.stringify(envelope));
    return post('/items', {
      encrypted_blob: enc.encrypted_blob,
      wrapped_key: enc.wrapped_key,
      nonce: enc.nonce,
      item_type: 'encrypted',
      metadata: {},
    });
  },

  async getItem(id) {
    return get(`/items/${id}`);
  },

  async deleteItem(id) {
    return del(`/items/${id}`);
  },

  decryptItem(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion) {
    return decryptItem(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion);
  },

  decryptItemRaw(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion) {
    return decryptItemRaw(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion);
  },

  encryptFileBytes(uint8Array) {
    return encryptFileBytes(uint8Array);
  },

  decryptFileBytes(uint8Array, wrappedKey, nonce) {
    return decryptFileBytes(uint8Array, wrappedKey, nonce);
  },

  // Groups
  async listGroups() {
    return get('/groups');
  },

  async getGroupItems(groupId) {
    return get(`/groups/${groupId}/items`);
  },

  // USB-specific (no-ops in web mode)
  async detectDrives() { return []; },
  async initUsb() { throw new Error('USB storage requires the desktop app'); },
  async syncNow() { throw new Error('Sync requires the desktop app'); },
  async syncStatus() { return { unlocked: false, usb_connected: false, has_pin: false, item_count: 0 }; },
};

// ─── Tauri backend (native Rust) ─────────────────────────────────────────────

const tauriBackend = {
  mode: 'tauri',

  async init() {
    // WASM still needed for web-only flows (grants, drops) in desktop app
    await initWasm();
  },

  hasKeys() {
    // Check Tauri backend state
    // For sync purposes, also check WASM keys (used for grant/drop flows)
    return hasKeys();
  },

  async unlock(password, salt, encryptedPrivateKey, pubKey, encryptedMasterKey, opts = {}) {
    // Derive keys in WASM too (for grant/drop flows that still use JS crypto)
    deriveAndStoreKeys(password, salt, encryptedPrivateKey, pubKey, encryptedMasterKey);

    // Also unlock the Tauri Rust backend
    const userId = getUserId();
    await invoke('unlock', {
      password,
      salt: Array.from(new Uint8Array(salt)),
      encrypted_private_key: Array.from(new Uint8Array(encryptedPrivateKey)),
      encrypted_private_key_nonce: Array.from(new Uint8Array(encryptedPrivateKey).slice(0, 24)),
      public_key: Array.from(new Uint8Array(pubKey)),
      encrypted_master_key: Array.from(new Uint8Array(encryptedMasterKey)),
      encrypted_master_key_nonce: Array.from(new Uint8Array(encryptedMasterKey).slice(0, 24)),
      user_id: userId,
      email: opts.email || '',
      pin: opts.pin || null,
    });
  },

  async lock() {
    clearKeys();
    await invoke('lock');
  },

  // Items — routes to Rust backend when USB is connected, falls back to HTTP
  async listItems() {
    const status = await invoke('vault_status');
    if (status.usb_connected && status.unlocked) {
      return invoke('list_items');
    }
    return get('/items');
  },

  async createItem(envelope) {
    const status = await invoke('vault_status');
    if (status.usb_connected && status.unlocked) {
      const plaintext = new TextEncoder().encode(JSON.stringify(envelope));
      const itemId = await invoke('create_item', {
        plaintext: Array.from(plaintext),
        item_type: envelope.item_type || 'encrypted',
      });
      return { id: itemId };
    }
    // Fall back to web flow
    const enc = encryptItem(JSON.stringify(envelope));
    return post('/items', {
      encrypted_blob: enc.encrypted_blob,
      wrapped_key: enc.wrapped_key,
      nonce: enc.nonce,
      item_type: 'encrypted',
      metadata: {},
    });
  },

  async getItem(id) {
    return get(`/items/${id}`);
  },

  async deleteItem(id) {
    const status = await invoke('vault_status');
    if (status.usb_connected && status.unlocked) {
      await invoke('delete_item', { item_id: id });
    }
    // Also delete from server
    return del(`/items/${id}`);
  },

  // Crypto — delegate to WASM (same as web, shared crypto.js functions)
  decryptItem(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion) {
    return decryptItem(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion);
  },

  decryptItemRaw(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion) {
    return decryptItemRaw(encryptedBlob, wrappedKey, nonce, wrapFormatVersion, blobFormatVersion);
  },

  encryptFileBytes(uint8Array) {
    return encryptFileBytes(uint8Array);
  },

  decryptFileBytes(uint8Array, wrappedKey, nonce) {
    return decryptFileBytes(uint8Array, wrappedKey, nonce);
  },

  // Groups — HTTP for now
  async listGroups() {
    return get('/groups');
  },

  async getGroupItems(groupId) {
    return get(`/groups/${groupId}/items`);
  },

  // USB management
  async detectDrives() {
    return invoke('detect_drives');
  },

  async initUsb(path, pin) {
    return invoke('init_usb', { path, pin: pin || null });
  },

  async setUsbPath(path) {
    return invoke('set_usb_path', { path });
  },

  async syncNow() {
    return invoke('sync_now');
  },

  async syncStatus() {
    return invoke('vault_status');
  },

  async setPin(pin) {
    return invoke('set_pin', { pin });
  },

  async removePin() {
    return invoke('remove_pin');
  },
};

// ─── Export the active backend ───────────────────────────────────────────────

export const backend = isTauri ? tauriBackend : webBackend;
